import { Text } from 'designSystem/components/Text'
import { useSubjectPhotoCounts } from 'features/fim/photos/useSubjectPhotoCounts'
import {
  getPageLayout,
  rectGeometry,
} from 'features/knowledgeDocumentViewer/components/viewer/search/aliasHighlightGeometry'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContextOptional } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import React, { type ReactNode, useLayoutEffect, useRef } from 'react'
import { useTransformContext, useTransformEffect } from 'react-zoom-pan-pinch'
import styled from 'styled-components'

import { GlassPill } from './glassSurface'

const Layer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`

const Anchor = styled.div.attrs<{ $left: string; $top: string }>(
  ({ $left, $top }) => ({ style: { left: $left, top: $top } })
)`
  position: absolute;
  width: 0;
  height: 0;
`

const ScaleOrigin = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
`

const CountPill = styled(GlassPill)`
  justify-content: center;
  min-width: 1.375rem;
  height: 1.375rem;
  padding: 0 0.375rem;
  color: var(--foreground);
  transform: translate(-50%, -50%);
`

const percent = (value: number, total: number): string =>
  `${(value / total) * 100}%`

const ZoomCounterScale: React.FC<{ children: ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { transformState } = useTransformContext()

  useLayoutEffect(() => {
    if (ref.current) {
      ref.current.style.transform = `scale(${1 / transformState.scale})`
    }
  }, [transformState])
  useTransformEffect(({ state }) => {
    if (ref.current) ref.current.style.transform = `scale(${1 / state.scale})`
  })

  return <ScaleOrigin ref={ref}>{children}</ScaleOrigin>
}

export const PhotoCountBadgeLayer: React.FC<{
  imageNatural: { w: number; h: number }
  firstPageWidth?: number
  isMultipage?: boolean
  counterZoom: boolean
}> = ({ imageNatural, firstPageWidth, isMultipage, counterZoom }) => {
  const { aliasHighlights, pageNumber, documentSubjects, siteID } =
    useDocumentTabDataContext()
  const uxContext = useKnowledgeDocumentUXContextOptional()
  const showBadges =
    (uxContext?.photoMode ?? false) && (uxContext?.showPhotoCounts ?? true)
  const photoCounts = useSubjectPhotoCounts(siteID, documentSubjects)

  if (!showBadges || photoCounts.size === 0) return null

  const badges = aliasHighlights.flatMap((highlight, index) => {
    const count = photoCounts.get(highlight.subjectUid)
    const onPage =
      highlight.page === pageNumber ||
      (isMultipage && highlight.page === pageNumber + 1)
    if (!count || !onPage) return []
    const isRightPage = isMultipage === true && highlight.page > pageNumber
    const { pageWidth, xOffset } = getPageLayout(
      isRightPage,
      imageNatural,
      firstPageWidth,
      isMultipage
    )
    const rect = rectGeometry(
      highlight.bbox,
      pageWidth,
      xOffset,
      imageNatural.h
    )
    return [
      {
        key: `${highlight.subjectUid}-${highlight.aliasNorm}-${index}`,
        count,
        left: percent(rect.x + rect.width, imageNatural.w),
        top: percent(rect.y, imageNatural.h),
      },
    ]
  })

  return (
    <Layer>
      {badges.map((badge) => {
        const content = (
          <CountPill data-photo-count={badge.count}>
            <Text variant='caption'>{badge.count}</Text>
          </CountPill>
        )
        return (
          <Anchor key={badge.key} $left={badge.left} $top={badge.top}>
            {counterZoom ? (
              <ZoomCounterScale>{content}</ZoomCounterScale>
            ) : (
              <ScaleOrigin>{content}</ScaleOrigin>
            )}
          </Anchor>
        )
      })}
    </Layer>
  )
}
